import {
  SPEECH_AUDIO_STATUSES,
  SPEECH_SYNTHESIS_FAILURE_REASONS,
  type SpeechClip,
  type SpeechSynthesisFailure,
  type SpeechSynthesisPort,
  type SpeechSynthesisRequest,
} from '@/shared-responder-v2-core';
import { base64ToAudioUrl } from '@/shared-utilities';

/**
 * ElevenLabs returns MPEG frames; the legacy default of `audio/wav` only ever
 * worked because browsers sniff the container.
 */
const SPEECH_AUDIO_MIME_TYPE = 'audio/mpeg';

const ABORTED_SPEECH_FAILURE: SpeechSynthesisFailure = {
  reason: SPEECH_SYNTHESIS_FAILURE_REASONS.Aborted,
  message: null,
  isRetryable: false,
};

export interface SpeechAudioRequest {
  readonly text: string;
  readonly language: string | null;
  readonly signal: AbortSignal;
}

export interface SpeechSynthesisPortInput {
  readonly language: string | null;
  readonly requestAudioData: (
    request: SpeechAudioRequest
  ) => Promise<string | null>;
}

export function base64ToBlobUrl(audioData: string): string {
  return base64ToAudioUrl(audioData, SPEECH_AUDIO_MIME_TYPE);
}

export function isSpeechAbortError(error: unknown): boolean {
  if (error instanceof DOMException) return error.name === 'AbortError';
  if (error instanceof Error) return error.name === 'AbortError';

  return false;
}

export function toSpeechSynthesisFailure(
  error: unknown
): SpeechSynthesisFailure {
  if (isSpeechAbortError(error)) {
    return ABORTED_SPEECH_FAILURE;
  }

  return {
    reason: SPEECH_SYNTHESIS_FAILURE_REASONS.RequestFailed,
    message: error instanceof Error ? error.message : null,
    isRetryable: true,
  };
}

export function toFailedSpeechClip(
  cacheKey: string,
  failure: SpeechSynthesisFailure
): SpeechClip {
  return {
    cacheKey,
    status: SPEECH_AUDIO_STATUSES.Failed,
    url: null,
    failure,
  };
}

export function toReadySpeechClip(cacheKey: string, url: string): SpeechClip {
  return {
    cacheKey,
    status: SPEECH_AUDIO_STATUSES.Ready,
    url,
    failure: null,
  };
}

function linkCallerSignal(
  controller: AbortController,
  signal?: AbortSignal
): void {
  if (!signal) return;
  if (signal.aborted) {
    controller.abort();
    return;
  }

  signal.addEventListener('abort', () => controller.abort(), { once: true });
}

const SPEECH_CLIP_CACHE_LIMIT = 12;

interface CachedSpeechClip {
  readonly clip: SpeechClip;
  readonly text: string;
  readonly language: string | null;
}

interface PendingSpeechRequest {
  readonly text: string;
  readonly language: string | null;
  readonly controller: AbortController;
  readonly promise: Promise<SpeechClip>;
}

function toAbortedSpeechClip(cacheKey: string): SpeechClip {
  return toFailedSpeechClip(cacheKey, ABORTED_SPEECH_FAILURE);
}

function joinPendingRequest(
  cacheKey: string,
  pendingRequest: PendingSpeechRequest,
  signal?: AbortSignal
): Promise<SpeechClip> {
  if (!signal) return pendingRequest.promise;
  if (signal.aborted) return Promise.resolve(toAbortedSpeechClip(cacheKey));

  return new Promise<SpeechClip>((resolve) => {
    const detach = () => resolve(toAbortedSpeechClip(cacheKey));

    signal.addEventListener('abort', detach, { once: true });
    void pendingRequest.promise.then((clip) => {
      signal.removeEventListener('abort', detach);
      resolve(clip);
    });
  });
}

export function createSpeechSynthesisPort({
  language,
  requestAudioData,
}: SpeechSynthesisPortInput): SpeechSynthesisPort {
  const clips = new Map<string, CachedSpeechClip>();
  const pending = new Map<string, PendingSpeechRequest>();
  const controllers = new Set<AbortController>();
  const holds = new Map<string, number>();
  const deferredUrls = new Map<string, string[]>();

  function revoke(cacheKey: string, url: string): void {
    if ((holds.get(cacheKey) ?? 0) === 0) {
      URL.revokeObjectURL(url);
      return;
    }

    deferredUrls.set(cacheKey, [...(deferredUrls.get(cacheKey) ?? []), url]);
  }

  function evict(cacheKey: string): void {
    const cached = clips.get(cacheKey);

    if (cached?.clip.url) revoke(cacheKey, cached.clip.url);
    clips.delete(cacheKey);
  }

  function hold(cacheKey: string): () => void {
    holds.set(cacheKey, (holds.get(cacheKey) ?? 0) + 1);

    let isReleased = false;

    return () => {
      if (isReleased) return;
      isReleased = true;

      const remaining = (holds.get(cacheKey) ?? 1) - 1;

      if (remaining > 0) {
        holds.set(cacheKey, remaining);
        return;
      }

      holds.delete(cacheKey);

      for (const url of deferredUrls.get(cacheKey) ?? []) {
        URL.revokeObjectURL(url);
      }

      deferredUrls.delete(cacheKey);
    };
  }

  function trimReadyClips(): void {
    const readyKeys = [...clips]
      .filter(
        ([, cached]) => cached.clip.status === SPEECH_AUDIO_STATUSES.Ready
      )
      .map(([cacheKey]) => cacheKey);

    for (const cacheKey of readyKeys.slice(
      0,
      Math.max(0, readyKeys.length - SPEECH_CLIP_CACHE_LIMIT)
    )) {
      evict(cacheKey);
    }
  }

  function cancelInFlight(): void {
    for (const controller of controllers) controller.abort();
    controllers.clear();
    pending.clear();
  }

  function abortAll(): void {
    cancelInFlight();

    for (const [cacheKey, cached] of clips) {
      if (cached.clip.status !== SPEECH_AUDIO_STATUSES.Ready) evict(cacheKey);
    }
  }

  function dispose(): void {
    cancelInFlight();
    holds.clear();

    for (const cacheKey of clips.keys()) evict(cacheKey);

    for (const urls of deferredUrls.values()) {
      for (const url of urls) URL.revokeObjectURL(url);
    }

    deferredUrls.clear();
  }

  function startRequest(
    speechRequest: SpeechSynthesisRequest,
    resolvedLanguage: string | null,
    controller: AbortController
  ): PendingSpeechRequest {
    const { cacheKey, text } = speechRequest;

    function owns(): boolean {
      return pending.get(cacheKey)?.controller === controller;
    }

    function store(clip: SpeechClip): SpeechClip {
      clips.set(cacheKey, { clip, text, language: resolvedLanguage });
      return clip;
    }

    function abandon(): SpeechClip {
      if (owns()) clips.delete(cacheKey);
      return toAbortedSpeechClip(cacheKey);
    }

    async function run(): Promise<SpeechClip> {
      try {
        const audioData = await requestAudioData({
          language: resolvedLanguage,
          signal: controller.signal,
          text,
        });

        if (controller.signal.aborted) {
          return abandon();
        }

        if (!audioData) {
          return store(
            toFailedSpeechClip(cacheKey, {
              reason: SPEECH_SYNTHESIS_FAILURE_REASONS.NoAudioReturned,
              message: null,
              isRetryable: false,
            })
          );
        }

        const clip = store(
          toReadySpeechClip(cacheKey, base64ToBlobUrl(audioData))
        );

        trimReadyClips();

        return clip;
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          return abandon();
        }

        return store(
          toFailedSpeechClip(cacheKey, toSpeechSynthesisFailure(error))
        );
      }
    }

    return { text, language: resolvedLanguage, controller, promise: run() };
  }

  function synthesize(
    speechRequest: SpeechSynthesisRequest
  ): Promise<SpeechClip> {
    const { cacheKey, text } = speechRequest;
    const resolvedLanguage = speechRequest.language ?? language;
    const cached = clips.get(cacheKey);

    if (cached?.clip.status === SPEECH_AUDIO_STATUSES.Ready) {
      if (cached.text === text && cached.language === resolvedLanguage) {
        clips.delete(cacheKey);
        clips.set(cacheKey, cached);
        return Promise.resolve(cached.clip);
      }

      evict(cacheKey);
    }

    const inFlight = pending.get(cacheKey);

    if (inFlight?.text === text && inFlight.language === resolvedLanguage) {
      return joinPendingRequest(cacheKey, inFlight, speechRequest.signal);
    }

    inFlight?.controller.abort();

    const controller = new AbortController();

    linkCallerSignal(controller, speechRequest.signal);

    if (controller.signal.aborted) {
      return Promise.resolve(toAbortedSpeechClip(cacheKey));
    }

    controllers.add(controller);
    clips.set(cacheKey, {
      clip: {
        cacheKey,
        status: SPEECH_AUDIO_STATUSES.Loading,
        url: null,
        failure: null,
      },
      text,
      language: resolvedLanguage,
    });

    const entry = startRequest(speechRequest, resolvedLanguage, controller);

    pending.set(cacheKey, entry);
    void entry.promise.then(() => {
      controllers.delete(controller);
      if (pending.get(cacheKey) === entry) pending.delete(cacheKey);
    });

    return entry.promise;
  }

  return {
    language,
    clipFor: (cacheKey: string) => clips.get(cacheKey)?.clip ?? null,
    synthesize,
    hold,
    evict,
    abortAll,
    dispose,
  };
}
